import { computed, onMounted, ref, unref, type MaybeRef } from 'vue';

/**
 * Creates a client-only gate around a reactive boolean for use in templates.
 *
 * Use this to avoid SSR mismatches and cache leaks when SSR output depends on
 * user-specific data, while still reacting to login state or other refs.
 *
 * **Example:**
 * ```ts
 * const isLoggedInClient = useClientGate(isLoggedIn);
 * const isClient = useClientGate(true);
 * ```
 *
 * **Extended example:**
 * ```vue
 * <script setup lang="ts">
 * const { isLoggedIn } = useLoginState();
 * const isLoggedInClient = useClientGate(isLoggedIn);
 * </script>
 *
 * <template>
 *   <div v-if="isLoggedInClient">
 *     <UserGreeting />
 *   </div>
 *   <LoginTeaser v-else />
 * </template>
 * ```
 *
 * **Extended example (client-only side effects):**
 * This pattern is handy when you need to hydrate client components with
 * dynamic, user-specific content after the initial SSR render.
 * ```ts
 * const isLoggedInClient = useClientGate(isLoggedIn);
 *
 * watch(isLoggedInClient, (isClientAndLoggedIn) => {
 *     if (!isClientAndLoggedIn) return;
 *
 *     hydrateUserOnlyWidget();
 * });
 * ```
 *
 * @param value - A boolean or ref to gate with the client check.
 * @returns A computed boolean that is true only on the client and when `value` is truthy.
 */
export function useClientGate(value: MaybeRef<boolean>) {
    const isMounted = ref(false);

    onMounted(() => {
        isMounted.value = true;
    });

    return computed(() => isMounted.value && Boolean(unref(value)));
}
